import React, { useRef, useEffect } from 'react';
import { motion, useAnimation, useInView } from 'framer-motion';
import {   
  FaGraduationCap,   
  FaLaptop,   
  FaChalkboardTeacher,  
  FaClock,  
  FaCheck,  
  FaHeadset,  
  FaBriefcase,  
  FaUserGraduate,  
  FaBookOpen, 
  FaUsers 
} from 'react-icons/fa';

// Animation variants
const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.2,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  },
};

// Animation variants removed as they were not being used

// Animated Section Component
const AnimatedSection = ({ children, className = "", delay = 0 }) => {
  const controls = useAnimation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  useEffect(() => {
    if (isInView) {
      controls.start("show");
    }
  }, [controls, isInView]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 20 },
        show: {
          opacity: 1,
          y: 0,
          transition: {
            duration: 0.6,
            delay: delay,
            ease: "easeOut"
          }
        }
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const SmartLearningSection = () => {
  const features = [
    {
      icon: <FaLaptop className="w-12 h-12 mx-auto mb-4 text-[#800000]" />,
      title: 'Study Anytime and Anywhere',
      description: 'You don\'t have to attend our College campus, so you can study from anywhere in the world. Our online delivery enables you to download the lecture notes, submit assignments wherever you are.'
    },
    {
      icon: <FaChalkboardTeacher className="w-12 h-12 mx-auto mb-4 text-[#800000]" />,
      title: 'Direct Faculty Interaction',
      description: 'All programmes come with direct faculty / lecturer support using discussion forum and dedicated chat support.'
    },
    {
      icon: <FaUsers className="w-12 h-12 mx-auto mb-4 text-[#800000]" />,
      title: 'Discussion Forum',
      description: 'We enable you to be an independent learner, supported by your academic tutor, support staff and fellow students through an online community, using high-quality learning materials.'
    }
  ];

  return (
    <AnimatedSection className="py-16 bg-gray-50" delay={0.5}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl mb-6">
            World's Best Online Education Experience
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            All our pre-masters and masters qualifications are recognised and well accepted around the world – from universities to equivalent bodies and employers worldwide.
          </p>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-12">
            Our unique learning methodology enables you to complete your programmes at your own pace and your time...
          </p>
          
          <h3 className="text-2xl font-bold text-[#800000] mb-10">About Smart Learning</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div 
              key={index}
              className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 flex flex-col h-full"
              variants={item}
              whileHover={{ scale: 1.02 }}
            >
              <div className="flex justify-center mb-6">
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">
                {feature.title}
              </h3>
              <p className="text-gray-600 text-center flex-grow">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
};

const AboutLawrenceOnline = () => {
  const features = [
    {      
      icon: <FaGraduationCap className="w-12 h-12 mx-auto mb-4 text-[#800000]" />,      
      title: 'Accredited Programs',      
      description: 'Globally recognized degrees and certifications from an accredited institution'    },    
    {      
      icon: <FaLaptop className="w-12 h-12 mx-auto mb-4 text-[#800000]" />,      
      title: 'Interactive Learning',      
      description: 'Engaging multimedia content, live sessions, and collaborative tools'    },    
    {      
      icon: <FaClock className="w-12 h-12 mx-auto mb-4 text-[#800000]" />,      
      title: 'Flexible Schedule',      
      description: '24/7 access to course materials to fit your busy lifestyle'    },    
    {      
      icon: <FaChalkboardTeacher className="w-12 h-12 mx-auto mb-4 text-[#800000]" />,      
      title: 'Expert Faculty',      
      description: 'Learn from industry leaders and experienced academic professionals'    }  ];  
  
  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1">
      {/* Hero Section */}
      <AnimatedSection className="relative bg-gradient-to-r from-primary to-primary-dark text-white py-20 md:py-32">        <div className="absolute inset-0 overflow-hidden">          <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/40"></div>        </div>        <div className="relative max-w-7xl mx-auto px-4 py-24 sm:py-32 text-center">          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 leading-tight">            Welcome to <span className="text-white">FIC College</span>          </h1>          <p className="text-xl md:text-2xl text-white max-w-4xl mx-auto mb-10">            Transform your future with flexible, high-quality education from anywhere in the world.            Experience academic excellence on your schedule.          </p>        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-gray-50 to-transparent"></div>
      </AnimatedSection>      {/* Features Section */}
      <AnimatedSection className="py-16 bg-white" delay={0.2}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Why Choose FIC College
            </h2>
            <p className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto">
              Experience education that fits your life and helps you achieve your goals
            </p>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
                variants={item}
                whileHover={{ scale: 1.02 }}
              >
                <div className="flex justify-center">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mt-4 mb-3 text-center">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-center">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </AnimatedSection>
      
      {/* About Section */}
      <AnimatedSection className="py-16 bg-gray-50" delay={0.3}>        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">          <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">            <div className="mb-12 lg:mb-0">              <div className="relative rounded-2xl overflow-hidden shadow-xl">                <img                  src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1471&q=80"                  alt="Students learning online"                  className="w-full h-auto"                />                <div className="absolute -bottom-6 -right-6 bg-[#800000] text-white p-6 rounded-lg shadow-lg w-48">                  <div className="text-3xl font-bold">10+</div>                  <div className="text-sm">Years of Excellence in Online Education</div>                </div>              </div>            </div>            <div>              <h2 className="text-3xl font-extrabold text-gray-900 mb-6">                Redefining Education for the Digital Age              </h2>              <div className="prose prose-lg text-gray-600 mb-8">                <p className="mb-6">                  FIC Online brings the excellence of FIC College's academic programs to students worldwide.                   Our innovative platform combines cutting-edge technology with our renowned curriculum to deliver an                   exceptional educational experience that prepares you for success in today's global marketplace.                </p>                <p className="mb-6">                  Whether you're looking to advance your career, change professions, or pursue personal growth,                   our flexible online programs are designed to fit your busy schedule while maintaining the same                   academic rigor as our on-campus programs.                </p>              </div>              <div className="space-y-4">
                {[
                  'Fully accredited degree programs',
                  'Interactive virtual classrooms',
                  'Personalized academic support',
                  'Career-focused curriculum',
                  '24/7 access to learning materials'
                ].map((item, index) => (
                  <div key={index} className="flex items-start">
                    <FaCheck className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>      {/* How It Works Section */}
      <AnimatedSection className="py-16 bg-white" delay={0.4}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl mb-4">
              How Online Learning Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our simple, student-centered approach makes online learning effective and enjoyable
            </p>
          </div>
          <div className="relative">
            {/* Timeline line */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-1 bg-blue-100 transform -translate-x-1/2"></div>
            
            {/* Steps */}
            <motion.div 
              className="space-y-12"
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
            >
              {[
                {
                  step: '01',
                  title: 'Enroll in Your Program',
                  description: 'Choose from our range of accredited online programs and complete your application.',
                  icon: <FaUserGraduate className="w-6 h-6 text-[#800000]" />
                },
                {
                  step: '02',
                  title: 'Access Course Materials',
                  description: 'Log in to our learning management system to access lectures, readings, and assignments.',
                  icon: <FaLaptop className="w-6 h-6 text-[#800000]" />
                },
                {
                  step: '03',
                  title: 'Engage and Learn',
                  description: 'Participate in live sessions, discussion forums, and interactive activities with peers and instructors.',
                  icon: <FaBookOpen className="w-6 h-6 text-[#800000]" />
                },
                {
                  step: '04',
                  title: 'Complete Assessments',
                  description: 'Submit assignments, take quizzes, and complete projects to demonstrate your learning.',
                  icon: <FaCheck className="w-6 h-6 text-[#800000]" />
                },
                {
                  step: '05',
                  title: 'Earn Your Credential',
                  description: 'Successfully complete your program and receive your accredited qualification.',
                  icon: <FaGraduationCap className="w-6 h-6 text-[#800000]" />
                }
              ].map((item, index) => (
                <motion.div 
                  key={index} 
                  className="relative flex flex-col md:flex-row items-center"
                  variants={item}
                >
                  {/* Left side (for even steps) */}
                  <div className="hidden md:block md:w-1/2 pr-16 text-right">
                    {index % 2 === 0 && (
                      <motion.div 
                        className="py-4 px-6 bg-white rounded-lg shadow-sm border border-gray-100"
                        whileHover={{ scale: 1.03 }}
                      >
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                        <p className="text-gray-600">{item.description}</p>
                      </motion.div>
                    )}
                  </div>
                  
                  {/* Step number */}
                  <motion.div 
                    className="relative z-10 flex items-center justify-center w-16 h-16 rounded-full bg-[#800000] text-white font-bold text-xl mb-4 md:mb-0"
                    whileHover={{ scale: 1.1 }}
                  >
                    {React.cloneElement(item.icon, { className: 'w-6 h-6 text-white' })}
                  </motion.div>
                  
                  {/* Right side (for odd steps) */}
                  <div className="hidden md:block md:w-1/2 pl-16">
                    {index % 2 !== 0 && (
                      <motion.div 
                        className="py-4 px-6 bg-white rounded-lg shadow-sm border border-gray-100"
                        whileHover={{ scale: 1.03 }}
                      >
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                        <p className="text-gray-600">{item.description}</p>
                      </motion.div>
                    )}
                  </div>
                  
                  {/* Mobile view (always show below) */}
                  <div className="md:hidden w-full mt-4 pl-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </AnimatedSection>
      
      {/* Student Support Section */}
      <AnimatedSection className="py-16 bg-gray-50" delay={0.5}>        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">          <div className="text-center mb-16">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl mb-4">
              Dedicated Student Support
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We provide comprehensive support services to ensure your academic success
            </p>
          </div>
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
          >
            {[
              {
                icon: <FaUserGraduate className="w-8 h-8 text-blue-600" />,
                title: 'Academic Advising',
                description: 'Personalized guidance from enrollment to graduation',
                link: '/support/academic-advising'
              },
              {
                icon: <FaHeadset className="w-8 h-8 text-blue-600" />,
                title: '24/7 Technical Help',
                description: 'Round-the-clock support for all your technical needs',
                link: '/support/technical-help'
              },
              {
                icon: <FaBookOpen className="w-8 h-8 text-blue-600" />,
                title: 'Learning Resources',
                description: 'Access to digital library and research materials',
                link: '/resources'
              },
              {
                icon: <FaBriefcase className="w-8 h-8 text-blue-600" />,
                title: 'Career Services',
                description: 'Job placement assistance and career counseling',
                link: '/career-services'
              }
            ].map((item, index) => (
              <motion.div 
                key={index}
                className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
                variants={item}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
              >
                <div className="w-16 h-16 flex items-center justify-center bg-[#800000] rounded-full mb-6 mx-auto">
                  {React.cloneElement(item.icon, { className: 'w-8 h-8 text-white' })}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-center">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
          <motion.div 
            className="mt-16 bg-[#800000] rounded-2xl p-8 md:p-12 text-white"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="max-w-4xl mx-auto text-center">
              <h3 className="text-2xl md:text-3xl font-bold mb-4 text-white">Need Help? We're Here for You</h3>
              <p className="text-white text-opacity-90 text-lg mb-8 max-w-3xl mx-auto">
                Our dedicated support team is ready to assist you with any questions about our programs, admissions, or technical issues.
              </p>
            </div>
          </motion.div>
        </div>
      </AnimatedSection>      {/* CTA Section */}
      <AnimatedSection className="relative py-20 overflow-hidden" delay={0.6}>
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1471&q=80')] bg-cover bg-center"></div>        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-6">            Ready to Transform Your Future?          </h2>          <p className="text-xl text-white mb-10 max-w-3xl mx-auto">            Join thousands of successful graduates who launched their careers with FIC College. Take the first step toward your goals today.          </p>
        </div>
      </AnimatedSection>
      </main>
      
      {/* Smart Learning Section */}
      <SmartLearningSection />
    </div>
  );
};

export default AboutLawrenceOnline;